<template>
  <TexturedCard class="release-card">
    <div class="release-card__header">
      <div class="release-card__cover">
        <img
          v-if="showCover"
          :src="coverUrl"
          :alt="`${release.title} cover`"
          loading="lazy"
          @error="onCoverError"
        >
        <div
          v-else
          class="release-card__cover-fallback"
        >
          <span>{{ release.title }}</span>
        </div>
      </div>

      <div class="release-card__info">
        <div class="release-card__title-row">
          <h2 class="release-card__title">
            {{ release.title }}
          </h2>
          <div
            v-if="tagItems.length"
            class="release-card__tags"
          >
            <span
              v-for="tag in tagItems"
              :key="tag"
              class="release-card__tag"
            >
              {{ tag }}
            </span>
          </div>
        </div>

        <ul
          v-if="metaItems.length"
          class="release-card__meta"
        >
          <li
            v-for="item in metaItems"
            :key="item.label"
          >
            <span class="release-card__meta-label">{{ item.label }}:</span>
            <span>{{ item.value }}</span>
          </li>
        </ul>

        <p
          v-if="release.originalArtist"
          class="release-card__note"
        >
          Remix of
          <a
            v-if="release.originalArtist.url"
            :href="release.originalArtist.url"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ release.originalArtist.name }}
          </a>
          <span v-else>{{ release.originalArtist.name }}</span>
        </p>

        <div
          v-if="creditLines.length"
          class="release-card__credits"
        >
          <p
            v-for="credit in creditLines"
            :key="credit"
          >
            {{ credit }}
          </p>
        </div>
      </div>
    </div>

    <div class="release-card__body">
      <div
        v-if="tracklist.length"
        class="release-card__section release-card__section--wide release-card__section--tracklist"
      >
        <h3 class="release-card__section-title">
          Tracklist
        </h3>
        <ol class="release-card__tracklist">
          <li
            v-for="(track, index) in tracklist"
            :key="`${track}-${index}`"
          >
            {{ track }}
          </li>
        </ol>
      </div>

      <div
        v-if="platformItems.length"
        class="release-card__section"
      >
        <h3 class="release-card__section-title">
          Listen / Watch
        </h3>
        <SocialIcons
          :platforms="platformItems"
          theme="mono"
          size="md"
          spacing="tight"
        />
      </div>

      <div
        v-if="youtubeId"
        class="release-card__section release-card__section--wide"
      >
        <h3 class="release-card__section-title">
          Video
        </h3>
        <div class="release-card__video">
          <VideoThumb
            v-if="!showVideo"
            :video-id="youtubeId"
            :title="release.title"
            @openVideo="showVideo = true"
          />
          <div
            v-else
            class="release-card__video-frame"
          >
            <ResponsiveVideoEmbed :video-id="youtubeId" />
          </div>
          <button
            v-if="showVideo"
            class="release-card__video-toggle"
            type="button"
            @click="showVideo = false"
          >
            Hide video
          </button>
        </div>
      </div>

      <div
        v-if="purchaseItems.length"
        class="release-card__section"
      >
        <h3 class="release-card__section-title">
          Purchase
        </h3>
        <div class="release-card__links">
          <a
            v-for="purchase in purchaseItems"
            :key="purchase.key"
            :href="purchase.url"
            :target="purchase.target"
            :rel="purchase.rel"
            class="release-card__link release-card__link--purchase"
          >
            {{ purchase.label }}
          </a>
        </div>
      </div>

      <div
        v-if="release.soundcloudEmbed"
        class="release-card__section release-card__section--wide"
      >
        <h3 class="release-card__section-title">
          Listen
        </h3>
        <div class="release-card__embed">
          <iframe
            :src="release.soundcloudEmbed"
            title="Soundcloud player"
            loading="lazy"
            allow="autoplay"
          />
        </div>
      </div>
    </div>
  </TexturedCard>
</template>

<script setup>
import { computed, ref } from 'vue';
import TexturedCard from '@/components/effects/TexturedCard.vue';
import SocialIcons from '@/components/common/SocialIcons.vue';
import VideoThumb from '@/components/common/VideoThumb.vue';
import ResponsiveVideoEmbed from '@/components/common/ResponsiveVideoEmbed.vue';
import { resolveImageUrl } from '@/utils/assetPaths';

const props = defineProps({
  release: {
    type: Object,
    required: true,
  },
});

const coverError = ref(false);
const showVideo = ref(false);

const coverUrl = computed(() => resolveImageUrl(props.release?.cover));

const showCover = computed(() => coverUrl.value && !coverError.value);

const onCoverError = () => {
  coverError.value = true;
};

const formatKey = (value) =>
  value
    .toString()
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase());

const tagItems = computed(() => {
  const tags = [];
  const formatValue = props.release?.format?.trim();
  const typeValue = props.release?.type?.trim();

  if (formatValue) {
    tags.push(formatValue);
  }

  if (
    typeValue &&
    (!formatValue || typeValue.toLowerCase() !== formatValue.toLowerCase())
  ) {
    tags.push(formatKey(typeValue));
  }

  return tags;
});

const metaItems = computed(() => {
  const release = props.release || {};
  const items = [];

  if (release.year) items.push({ label: 'Year', value: release.year });
  if (release.releaseDate) {
    items.push({ label: 'Release date', value: release.releaseDate });
  }
  if (release.label) items.push({ label: 'Label', value: release.label });
  if (release.catalogNumber) {
    items.push({ label: 'Catalog', value: release.catalogNumber });
  }
  if (release.director) {
    items.push({ label: 'Director', value: release.director });
  }

  return items;
});

const tracklist = computed(() => {
  const list = props.release?.tracklist || [];
  return Array.isArray(list)
    ? list.map((track) => (track ? track.trim() : '')).filter(Boolean)
    : [];
});

const getYoutubeId = (url) => {
  if (!url) return '';
  if (/^[\w-]{11}$/.test(url)) return url;
  const shortMatch = url.match(/youtu\.be\/([\w-]+)/i);
  if (shortMatch?.[1]) return shortMatch[1];
  const watchMatch = url.match(/[?&]v=([\w-]+)/i);
  if (watchMatch?.[1]) return watchMatch[1];
  const embedMatch = url.match(/\/embed\/([\w-]+)/i);
  if (embedMatch?.[1]) return embedMatch[1];
  return '';
};

const youtubeId = computed(() => getYoutubeId(props.release?.links?.youtube));

const isExternal = (url) => /^https?:\/\//i.test(url);

const toLink = (key, label, url) => ({
  key,
  label,
  url,
  target: isExternal(url) ? '_blank' : undefined,
  rel: isExternal(url) ? 'noopener noreferrer' : undefined,
});

const linkOrder = [
  { key: 'bandcamp', label: 'Bandcamp' },
  { key: 'spotify', label: 'Spotify' },
  { key: 'apple', label: 'Apple Music' },
  { key: 'itunes', label: 'iTunes' },
  { key: 'tidal', label: 'Tidal' },
  { key: 'amazon', label: 'Amazon' },
  { key: 'discogs', label: 'Discogs' },
  { key: 'soundcloud', label: 'Soundcloud' },
  { key: 'youtube', label: 'YouTube' },
];

const platformItems = computed(() => {
  const links = props.release?.links || {};
  const items = [];
  const used = new Set();

  linkOrder.forEach(({ key, label }) => {
    if (links[key]) {
      items.push({ key, name: label, url: links[key] });
      used.add(key);
    }
  });

  Object.entries(links).forEach(([key, url]) => {
    if (!used.has(key) && url) {
      items.push({ key, name: formatKey(key), url });
    }
  });

  return items;
});

const purchaseItems = computed(() => {
  const purchase = props.release?.purchase || {};
  return Object.entries(purchase)
    .filter(([, item]) => item && item.url)
    .map(([key, item]) =>
      toLink(key, item.label || formatKey(key), item.url),
    );
});

const creditLines = computed(() => {
  const credits = props.release?.credits || [];
  if (!Array.isArray(credits)) return [];

  return credits
    .map((credit) => {
      if (!credit?.label || !credit?.value) return '';
      const joiner = credit.joiner || ': ';
      return `${formatKey(credit.label)}${joiner}${credit.value}`;
    })
    .filter(Boolean);
});
</script>

<style scoped>
.release-card {
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
}

.release-card__header {
  display: grid;
  grid-template-columns: minmax(170px, 240px) minmax(0, 1fr);
  gap: 1.75rem;
  align-items: start;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid var(--border-color);
}

.release-card__cover {
  position: relative;
  width: 100%;
  padding-top: 100%;
  border-radius: 0.75rem;
  border: 1px solid var(--border-color);
  background-color: var(--hover-color);
  overflow: hidden;
}

.release-card__cover img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.release-card__cover-fallback {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;
  text-align: center;
  color: var(--secondary-color);
  font-weight: 600;
  font-size: 0.9rem;
}

.release-card__info {
  display: grid;
  gap: 0.85rem;
}

.release-card__title-row {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.75rem;
}

.release-card__title {
  margin: 0;
  font-size: 1.85rem;
}

.release-card__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.release-card__tag {
  padding: 0.2rem 0.65rem;
  border-radius: 999px;
  border: 1px solid var(--border-color);
  background-color: var(--background-color);
  color: var(--card-text-color);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 0.7rem;
  font-weight: 600;
}

.release-card__meta {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 0.5rem 1.25rem;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
}

.release-card__meta li {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.release-card__meta-label {
  font-weight: 600;
  margin-right: 0.35rem;
}

.release-card__note {
  margin: 0;
}

.release-card__credits {
  margin-top: 0.25rem;
  display: grid;
  gap: 0.35rem;
  color: var(--secondary-color);
}

.release-card__body {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.5rem;
  align-items: start;
}

.release-card__section {
  display: grid;
  gap: 0.6rem;
}

.release-card__section--wide {
  grid-column: 1 / -1;
}

.release-card__section-title {
  margin: 0;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--secondary-color);
}

.release-card__tracklist {
  margin: 0;
  padding-left: 1.25rem;
  display: grid;
  gap: 0.35rem;
}

.release-card__links {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
}

.release-card__video {
  display: grid;
  gap: 0.75rem;
}

.release-card__video-frame {
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 0.75rem;
  overflow: hidden;
  border: 1px solid var(--border-color);
  background-color: var(--hover-color);
}

.release-card__video-toggle {
  align-self: flex-start;
  background-color: var(--button-bg-color);
  color: var(--button-text-color);
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  padding: 0.4rem 0.9rem;
  font-weight: 600;
  cursor: pointer;
}

.release-card__video-toggle:hover,
.release-card__video-toggle:focus-visible {
  background-color: var(--accent-color);
}

.release-card__link {
  display: inline-flex;
  align-items: center;
  padding: 0.4rem 0.85rem;
  border-radius: 0.5rem;
  background-color: var(--button-bg-color);
  color: var(--button-text-color);
  border: 1px solid var(--border-color);
  text-decoration: none;
  font-weight: 600;
  font-size: 0.85rem;
  transition: transform 0.1s ease, background-color 0.2s ease;
}

.release-card__link:hover,
.release-card__link:focus-visible {
  color: var(--accent-color);
  border-color: var(--accent-color);
  text-decoration: none;
}

.release-card__link--purchase {
  border-style: dashed;
}

.release-card__embed iframe {
  width: 100%;
  min-height: 200px;
  border: 1px solid var(--border-color);
  border-radius: 0.75rem;
}

@media (max-width: 720px) {
  .release-card__header {
    grid-template-columns: 1fr;
  }
}
</style>
